import { useMemo } from "react";
import { toggleSort as sharedToggleSort, type SortConfig } from "@/shared/utils";
import type { ContractorPayrollListItem, ContractorPayrollAggregatedStatus } from "../types";

export type SortKey = "period" | "contractors" | "totalGross" | "totalNet" | "paidCount" | "unpaidCount" | "status";

export type { SortConfig };

const STATUS_ORDER: Record<ContractorPayrollAggregatedStatus, number> = {
  all_pending: 0,
  partially_approved: 1,
  all_approved: 2,
  partially_paid: 3,
  all_paid: 4,
};

export function useContractorPayrollSort(
  items: ContractorPayrollListItem[],
  sortConfig: SortConfig<SortKey> | null,
): ContractorPayrollListItem[] {
  return useMemo(() => {
    if (!sortConfig) return items;

    const { key, direction } = sortConfig;

    return [...items].sort((a, b) => {
      let cmp = 0;

      switch (key) {
        case "period":
          cmp = a.year * 100 + a.month - (b.year * 100 + b.month);
          break;
        case "contractors":
          cmp = a.contractor_count - b.contractor_count;
          break;
        case "totalGross":
          cmp = parseFloat(a.total_gross) - parseFloat(b.total_gross);
          break;
        case "totalNet":
          cmp = parseFloat(a.total_net) - parseFloat(b.total_net);
          break;
        case "paidCount":
          cmp = a.paid_count - b.paid_count;
          break;
        case "unpaidCount":
          cmp = a.unpaid_count - b.unpaid_count;
          break;
        case "status":
          cmp = STATUS_ORDER[a.aggregated_status] - STATUS_ORDER[b.aggregated_status];
          break;
      }

      return direction === "asc" ? cmp : -cmp;
    });
  }, [items, sortConfig]);
}

export function toggleSort(currentSort: SortConfig<SortKey> | null, newKey: SortKey): SortConfig<SortKey> {
  return sharedToggleSort(currentSort, newKey);
}
